import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import { adminApi, PromptPreview } from '../api/admin';
import './PromptPreviewModal.css';

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * The `system_config` key the admin is currently editing. Determines
   * what `GET /api/admin/config/prompts/{key}/preview` returns — the
   * modal is agnostic about the family (classification, intent,
   * answer, summary, response, etc.) and just renders whatever the
   * backend produced.
   */
  promptKey: string | null;
}

type Tab = 'rendered' | 'template' | 'context';

const TAB_LABELS: Record<Tab, string> = {
  rendered: 'Randat',
  template: 'Șablon brut',
  context: 'Placeholder-uri ($)',
};

const SOURCE_LABELS: Record<PromptPreview['source'], string> = {
  live: 'Substituție live din taxonomie',
  stubs: 'Valori demo (date reale la rulare)',
  static: 'Text static — fără substituție',
};

/**
 * Generic preview for any `prompts.*` / `responses.*` value in
 * `system_config`. Calls `GET /api/admin/config/prompts/{key}/preview`
 * and presents the rendered output, raw template, and the value used
 * for each `$placeholder` so admins can:
 *
 *   - Verify the template still works after edits (`safe_substitute`
 *     silently leaves typos as literal `$foo`, so a visible preview is
 *     the only way to spot them before a real call).
 *   - Eyeball model-context size (char count).
 *   - For classification prompts, see exactly what the LLM gets — the
 *     placeholders are filled from the live taxonomy. For everything
 *     else, see the rendered shape with realistic stub data.
 */
const PromptPreviewModal: React.FC<Props> = ({ open, onClose, promptKey }) => {
  const [tab, setTab] = useState<Tab>('rendered');
  const [data, setData] = useState<PromptPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<Tab | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab('rendered');
    setCopied(null);
  }, [open, promptKey]);

  const load = useCallback(async () => {
    if (!promptKey) return;
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.previewPrompt(promptKey);
      setData(res);
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : 'Eroare la încărcarea previzualizării.';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [promptKey]);

  useEffect(() => {
    if (open && promptKey) void load();
  }, [open, promptKey, load]);

  const handleCopy = async (which: Tab, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      window.setTimeout(() => setCopied((cur) => (cur === which ? null : cur)), 1500);
    } catch {
      /* clipboard denied — silent: admin can still select manually */
    }
  };

  const renderBody = () => {
    if (loading) {
      return <p className="prompt-preview-status">Se încarcă previzualizarea…</p>;
    }
    if (error) {
      return (
        <div className="alert alert-error prompt-preview-error">
          <strong>Eroare:</strong> {error}
          <button type="button" className="btn btn-secondary btn-sm" onClick={load}>
            Reîncearcă
          </button>
        </div>
      );
    }
    if (!data) {
      return null;
    }
    if (tab === 'rendered') {
      return (
        <PromptPane
          label={`${data.key} (randat)`}
          text={data.rendered}
          chars={data.rendered_chars}
          copied={copied === 'rendered'}
          onCopy={() => handleCopy('rendered', data.rendered)}
          emptyHint={
            data.source === 'static'
              ? 'Acest mesaj nu folosește substituție — este trimis utilizatorului exact așa cum apare în câmpul de text.'
              : undefined
          }
        />
      );
    }
    if (tab === 'template') {
      return (
        <PromptPane
          label={`${data.key} (șablon brut din system_config)`}
          text={data.template}
          chars={data.template_chars}
          copied={copied === 'template'}
          onCopy={() => handleCopy('template', data.template)}
        />
      );
    }
    return <ContextPane context={data.context} source={data.source} />;
  };

  const visibleTabs: Tab[] = data?.is_template
    ? ['rendered', 'template', 'context']
    : ['rendered', 'template'];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Previzualizare prompt"
      headerExtra={
        <div className="prompt-preview-tabs" role="tablist">
          {visibleTabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`prompt-preview-tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
          {data && (
            <span
              className={`prompt-preview-source prompt-preview-source--${data.source}`}
              title={SOURCE_LABELS[data.source]}
            >
              {SOURCE_LABELS[data.source]}
            </span>
          )}
        </div>
      }
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={load} disabled={loading || !promptKey}>
            Refresh
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Închide
          </button>
        </>
      }
    >
      <div className="prompt-preview-body">{renderBody()}</div>
    </Modal>
  );
};

interface PromptPaneProps {
  label: string;
  text: string;
  chars: number;
  copied: boolean;
  onCopy: () => void;
  emptyHint?: string;
}

const PromptPane: React.FC<PromptPaneProps> = ({ label, text, chars, copied, onCopy, emptyHint }) => (
  <section className="prompt-preview-section">
    <header className="prompt-preview-section__header">
      <span className="prompt-preview-section__label">{label}</span>
      <span className="prompt-preview-section__chars">{chars.toLocaleString('ro-RO')} caractere</span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onCopy} disabled={!text}>
        {copied ? 'Copiat ✓' : 'Copiază'}
      </button>
    </header>
    {emptyHint && <p className="prompt-preview-hint">{emptyHint}</p>}
    <pre className="prompt-preview-pre">{text || '<gol>'}</pre>
  </section>
);

const ContextPane: React.FC<{ context: Record<string, string>; source: PromptPreview['source'] }> = ({
  context,
  source,
}) => {
  const entries = Object.entries(context).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return (
      <p className="prompt-preview-status">
        Acest prompt nu folosește placeholder-e <code>$</code> — nimic de substituit.
      </p>
    );
  }
  return (
    <div className="prompt-preview-context">
      <p className="prompt-preview-hint">
        {source === 'live' ? (
          <>
            Fiecare <code>$placeholder</code> este înlocuit cu textul de mai jos la fiecare apel — valori
            citite live din taxonomia activă.
          </>
        ) : (
          <>
            Fiecare <code>$placeholder</code> este înlocuit la rulare cu date reale (mesaj utilizator,
            documente regăsite, context membru). Valorile de aici sunt doar pentru previzualizare.
          </>
        )}
      </p>
      {entries.map(([key, value]) => (
        <details key={key} className="prompt-preview-context-item" open>
          <summary className="prompt-preview-context-item__header">
            <code className="prompt-preview-context-item__key">${key}</code>
            <span className="prompt-preview-context-item__chars">
              {value.length.toLocaleString('ro-RO')} caractere
            </span>
          </summary>
          <pre className="prompt-preview-pre prompt-preview-pre--small">{value || '<gol>'}</pre>
        </details>
      ))}
    </div>
  );
};

export default PromptPreviewModal;
